import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Users, CreditCard, Coins, Gift, Clock, ChevronRight, Heart } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useUser } from "../contexts/UserContext";
import { API } from "../lib/constants";
import AuthModal from "../components/AuthModal";

export default function StorePage() {
  const { isDark } = useTheme();
  const { user } = useUser();
  const navigate = useNavigate();
  const [packages, setPackages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState("");
  const [wallet, setWallet] = useState(null);
  const [history, setHistory] = useState(null);
  const [tab, setTab] = useState("store");
  const [showAuth, setShowAuth] = useState(false);
  const [donationAmount, setDonationAmount] = useState(2);
  const [customDonation, setCustomDonation] = useState("");
  const [donating, setDonating] = useState(false);
  const [donationStats, setDonationStats] = useState(null);

  const userToken = user?.user_id || localStorage.getItem("ris_user_token") || "anonymous";

  useEffect(() => {
    axios.get(`${API}/store/donations/stats`).then(r => setDonationStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pkgRes, walletRes] = await Promise.all([
          axios.get(`${API}/store/credit-packages`),
          axios.get(`${API}/store/wallet/${userToken}`),
        ]);
        setPackages(pkgRes.data);
        setWallet(walletRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [userToken]);

  const fetchHistory = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/store/payment-history/${userToken}`);
      setHistory(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (tab === "history" && !history) fetchHistory();
  }, [tab]);

  const handleBuyCredits = async (bundleId) => {
    if (!user) { setShowAuth(true); return; }
    setCheckoutLoading(bundleId);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/store/checkout/credits`, {
        bundle_id: bundleId, user_token: userToken, origin_url: origin,
      });
      if (res.data.url) {
        localStorage.setItem("ris_pending_session", res.data.session_id);
        window.location.href = res.data.url;
      }
    } catch (e) {
      console.error(e);
      alert("Checkout failed. Please try again.");
    } finally { setCheckoutLoading(""); }
  };

  const handleDonate = async () => {
    const amt = customDonation ? parseFloat(customDonation) : donationAmount;
    if (!amt || amt < 1 || amt > 500) {
      alert("Please choose an amount between $1 and $500.");
      return;
    }
    setDonating(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/store/checkout/donation`, {
        amount: amt, user_token: userToken, origin_url: origin, message: "",
      });
      if (res.data.url) {
        localStorage.setItem("ris_pending_session", res.data.session_id);
        window.location.href = res.data.url;
      }
    } catch (e) {
      console.error(e);
      alert("Donation checkout failed. Please try again.");
    } finally { setDonating(false); }
  };

  if (loading) {
    return (<div className="min-h-screen pt-24 flex items-center justify-center"><div className="w-12 h-12 border-2 border-red-600 border-t-transparent rounded-full animate-spin" /></div>);
  }

  const bundles = packages?.bundles ? Object.entries(packages.bundles) : [];

  return (
    <div className="min-h-screen pt-24 pb-32 px-6" data-testid="store-page">
      <div className="max-w-5xl mx-auto">
        <header className="text-center mb-8">
          <p className="font-mono text-red-500 text-sm tracking-[0.3em] mb-4">UNLOCK THE STORY</p>
          <h1 className="font-cinzel text-4xl md:text-5xl font-bold mb-4">CREDIT STORE</h1>
        </header>

        {/* Tabs */}
        {user && (
          <div className="flex justify-center gap-2 mb-10">
            {[
              { id: "store", label: "Store" },
              { id: "history", label: "My Account" },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  tab === t.id ? "bg-red-600 text-white" : isDark ? "bg-white/5 text-gray-400 hover:bg-white/10" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                data-testid={`tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {tab === "store" && (
          <>
            {/* Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12 max-w-3xl mx-auto">
              <div className={`p-6 rounded-lg border text-center ${isDark ? "border-white/10 bg-white/[0.02]" : "border-gray-200 bg-white"}`} data-testid="info-card-daily">
                <CreditCard size={28} className="mx-auto mb-3 text-cyan-400" />
                <h3 className="font-cinzel text-base font-bold mb-2">2 Free / 24Hrs</h3>
                <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Unlock 2 episodes for free every day</p>
              </div>
              <div className={`p-6 rounded-lg border text-center ${isDark ? "border-white/10 bg-white/[0.02]" : "border-gray-200 bg-white"}`} data-testid="info-card-credits">
                <Coins size={28} className="mx-auto mb-3 text-amber-400" />
                <h3 className="font-cinzel text-base font-bold mb-2">Buy Credits</h3>
                <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Credits unlock episodes, music streams & downloads</p>
              </div>
            </div>

            {/* Wallet Balance */}
            {wallet && wallet.credit_balance > 0 && (
              <div className={`mb-10 p-4 rounded-lg border text-center ${isDark ? "border-amber-500/30 bg-amber-950/20" : "border-amber-200 bg-amber-50"}`} data-testid="wallet-balance">
                <div className="flex items-center justify-center gap-3">
                  <Coins size={24} className="text-amber-500" />
                  <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Your Balance:</span>
                  <span className="font-anton text-3xl text-amber-500">{wallet.credit_balance}</span>
                  <span className={`text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>credits</span>
                </div>
              </div>
            )}

            {/* Not signed in prompt - HIDDEN until signup bonus is wired
            {!user && (
              <div className={`mb-10 p-6 rounded-lg border text-center ${isDark ? "border-green-500/20 bg-green-950/10" : "border-green-200 bg-green-50"}`} data-testid="signup-bonus-prompt">
                <Gift size={32} className="mx-auto mb-3 text-green-400" />
                <h3 className="font-cinzel text-lg font-bold mb-2">Sign Up & Start Reading Free</h3>
                <p className={`text-sm mb-4 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  Create a free account to instantly unlock the first <strong>10 episodes of The Red Ink Chronicles</strong> and <strong>5 episodes of Signed, Sealed, Betrayed</strong>.
                </p>
                <button onClick={() => setShowAuth(true)}
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl transition-all"
                  data-testid="signup-bonus-btn">
                  Sign Up Free
                </button>
              </div>
            )} */}

            {/* Credit Bundles */}
            <div className="mb-16">
              <h2 className="font-cinzel text-2xl text-center mb-6">Buy Credits</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {bundles.map(([id, bundle]) => {
                  const totalCredits = bundle.credits + bundle.bonus;
                  return (
                    <div key={id} className={`relative rounded-lg border p-5 text-center transition-all hover:border-red-500/40 ${isDark ? "border-white/10 bg-white/[0.02]" : "border-gray-200 bg-white"}`} data-testid={`bundle-${id}`}>
                      {bundle.bonus > 0 && (
                        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 bg-green-600 text-white text-[10px] font-bold tracking-wider rounded-full whitespace-nowrap">+{bundle.bonus} BONUS</div>
                      )}
                      <p className={`font-anton text-3xl mt-1 ${bundle.bonus > 0 ? "text-green-500" : "text-amber-500"}`}>{bundle.credits}</p>
                      <p className="font-cinzel text-sm font-bold mb-1">Credits</p>
                      <p className={`text-xs mb-3 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                        {bundle.bonus > 0 ? `${totalCredits} total` : `${bundle.credits} episode${bundle.credits !== 1 ? "s" : ""}`}
                      </p>
                      <p className="font-anton text-xl text-red-500 mb-3">${bundle.price.toFixed(2)}</p>
                      <button onClick={() => handleBuyCredits(id)} disabled={!!checkoutLoading}
                        className={`w-full py-2 text-sm font-bold rounded transition-colors ${isDark ? "bg-white/10 hover:bg-white/20 text-white" : "bg-gray-800 hover:bg-gray-900 text-white"}`}
                        data-testid={`buy-${id}-btn`}>
                        {checkoutLoading === id ? (<span className="flex items-center justify-center gap-2"><div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" /> ...</span>) : "Buy"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Subscription Plans (only show if backend has any) */}
            {packages?.subscriptions && Object.keys(packages.subscriptions).length > 0 && (
              <div className="mb-16" data-testid="subscriptions-section">
                <div className="text-center mb-6">
                  <p className="font-mono text-red-500 text-xs tracking-[0.3em] mb-2">COMING SOON</p>
                  <h2 className="font-cinzel text-2xl">Unlimited Pass</h2>
                  <p className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>One subscription. Every episode. No daily caps.</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
                  {Object.entries(packages.subscriptions).map(([id, plan]) => (
                    <div key={id}
                      className={`relative rounded-xl border p-6 transition-all ${plan.highlight
                        ? (isDark ? "border-red-500/50 bg-gradient-to-br from-red-950/40 to-[#0a0a0a] ring-1 ring-red-500/20" : "border-red-300 bg-red-50/50")
                        : (isDark ? "border-white/10 bg-white/[0.02]" : "border-gray-200 bg-white")
                      }`}
                      data-testid={`subscription-${id}`}
                    >
                      {plan.highlight && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-red-600 text-white text-[10px] font-bold tracking-[0.2em] rounded-full">
                          BEST VALUE
                        </div>
                      )}
                      <p className="font-cinzel text-lg font-bold mb-1">{plan.label}</p>
                      <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-500"}`}>{plan.tagline}</p>
                      <div className="mt-4 mb-5">
                        <span className={`font-anton text-4xl ${plan.highlight ? "text-red-500" : "text-amber-500"}`}>${plan.price.toFixed(2)}</span>
                        <span className={`text-sm ml-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>/ {plan.interval}</span>
                      </div>
                      <ul className={`space-y-1.5 text-sm mb-5 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        {(plan.features || []).map((f, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-red-500 mt-0.5">▸</span><span>{f}</span>
                          </li>
                        ))}
                      </ul>
                      <button disabled
                        className={`w-full py-2.5 text-sm font-bold rounded transition-colors ${isDark ? "bg-white/5 text-gray-500 cursor-not-allowed" : "bg-gray-100 text-gray-400 cursor-not-allowed"}`}
                        data-testid={`subscribe-${id}-btn`}
                        title="Subscriptions launch soon"
                      >
                        Notify Me at Launch
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Supporter Message */}
            <div className={`mb-12 p-8 rounded-lg border ${isDark ? "border-white/10 bg-white/[0.02]" : "border-gray-200 bg-gray-50"}`} data-testid="supporter-message">
              <h2 className="font-cinzel text-xl font-bold text-center mb-6">A Message to My Readers & Supporters</h2>
              <div className={`text-sm leading-relaxed space-y-4 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                <p>The fees listed in this book are more than numbers — they are what keep this platform alive, accessible, and continuously growing.</p>
                <p><strong className={isDark ? "text-gray-200" : "text-gray-800"}>Your support directly fuels:</strong></p>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  <li>Website hosting, security, and maintenance</li>
                  <li>Ongoing creation of guides, templates, and educational materials</li>
                  <li>Design, branding, and content development</li>
                  <li>Improvements, updates, and new features</li>
                  <li>The continued writing and expansion of this book series</li>
                </ul>
                <p className={`italic ${isDark ? "text-gray-300" : "text-gray-700"}`}>Thank you for being here, for reading, and for believing in the vision.</p>
                <p className="text-red-500 font-semibold text-center mt-4">Your generosity keeps this mission alive.</p>
              </div>

              {/* Tip / Donation Widget */}
              <div className={`mt-8 pt-8 border-t ${isDark ? "border-white/10" : "border-gray-200"}`} data-testid="donation-widget">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Heart size={16} className="text-red-500" fill="currentColor" />
                  <p className="font-mono text-red-500 text-[11px] tracking-[0.3em]">SUPPORT THE STUDIO</p>
                </div>
                <h3 className="font-cinzel text-xl text-center font-bold mb-2">Leave a Tip</h3>
                <p className={`text-center text-sm mb-6 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  Love what we're building? Every dollar fuels new chapters and audio production.
                </p>
                <div className="flex flex-wrap justify-center gap-2 mb-4">
                  {[1, 2, 5].map(amt => (
                    <button
                      key={amt}
                      onClick={() => { setDonationAmount(amt); setCustomDonation(""); }}
                      className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all border ${
                        !customDonation && donationAmount === amt
                          ? "bg-red-600 border-red-600 text-white scale-105"
                          : isDark
                            ? "border-white/15 bg-white/[0.03] text-gray-200 hover:border-red-500/40 hover:bg-white/[0.06]"
                            : "border-gray-300 bg-white text-gray-700 hover:border-red-500/40"
                      }`}
                      data-testid={`tip-${amt}-btn`}
                    >
                      ${amt}
                    </button>
                  ))}
                  <div className="flex items-center">
                    <span className={`px-3 py-2.5 text-sm border border-r-0 rounded-l-lg ${isDark ? "border-white/15 bg-white/[0.03] text-gray-400" : "border-gray-300 bg-gray-50 text-gray-500"}`}>$</span>
                    <input
                      type="number"
                      min="1"
                      max="500"
                      step="1"
                      placeholder="Other"
                      value={customDonation}
                      onChange={(e) => setCustomDonation(e.target.value)}
                      className={`w-24 px-3 py-2.5 text-sm border rounded-r-lg focus:outline-none focus:border-red-500 ${
                        isDark ? "border-white/15 bg-white/[0.03] text-white placeholder:text-gray-500" : "border-gray-300 bg-white text-gray-900"
                      }`}
                      data-testid="tip-custom-input"
                    />
                  </div>
                </div>
                <div className="flex justify-center">
                  <button
                    onClick={handleDonate}
                    disabled={donating}
                    className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-bold rounded-xl transition-all disabled:opacity-50 shadow-lg shadow-red-900/30"
                    data-testid="tip-submit-btn"
                  >
                    {donating ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Redirecting…
                      </span>
                    ) : (
                      <>
                        <Heart size={14} fill="currentColor" />
                        Tip ${customDonation || donationAmount}
                      </>
                    )}
                  </button>
                </div>
                <p className={`text-center text-[11px] mt-4 font-mono ${isDark ? "text-gray-600" : "text-gray-400"}`}>
                  SECURE CHECKOUT VIA STRIPE · ONE-TIME · NO ACCOUNT REQUIRED
                </p>
                {donationStats && donationStats.recent_count > 0 ? (
                  <p className={`text-center text-xs mt-3 ${isDark ? "text-gray-400" : "text-gray-500"}`} data-testid="tip-social-proof">
                    <Heart size={11} fill="currentColor" className="inline text-red-500 mr-1" />
                    Supported by <span className="font-bold text-red-500">{donationStats.recent_count}</span> reader{donationStats.recent_count !== 1 ? "s" : ""} in the last 30 days
                    {donationStats.total_count > donationStats.recent_count && (
                      <span className={isDark ? "text-gray-500" : "text-gray-400"}> · {donationStats.total_count} all-time</span>
                    )}
                  </p>
                ) : donationStats && donationStats.total_count === 0 ? (
                  <p className={`text-center text-xs mt-3 italic ${isDark ? "text-gray-400" : "text-gray-500"}`} data-testid="tip-first-supporter">
                    <Heart size={11} fill="currentColor" className="inline text-red-500 mr-1" />
                    Be the first reader to support the studio
                  </p>
                ) : null}
              </div>
            </div>
          </>
        )}

        {/* My Account / Payment History Tab */}
        {tab === "history" && user && (
          <div data-testid="account-section">
            {/* Account Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              <div className={`p-6 rounded-lg border text-center ${isDark ? "border-amber-500/20 bg-amber-950/10" : "border-amber-200 bg-amber-50"}`} data-testid="account-balance">
                <Coins size={28} className="mx-auto mb-2 text-amber-500" />
                <p className="font-anton text-4xl text-amber-500">{history?.credit_balance ?? wallet?.credit_balance ?? 0}</p>
                <p className={`text-xs mt-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>Credit Balance</p>
              </div>
              <div className={`p-6 rounded-lg border text-center ${isDark ? "border-white/10 bg-white/[0.02]" : "border-gray-200 bg-white"}`} data-testid="account-episodes">
                <CreditCard size={28} className="mx-auto mb-2 text-cyan-400" />
                <p className="font-anton text-4xl text-cyan-400">{history?.unlocked_episodes ?? 0}</p>
                <p className={`text-xs mt-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>Episodes Unlocked</p>
              </div>
              <div className={`p-6 rounded-lg border text-center ${isDark ? "border-white/10 bg-white/[0.02]" : "border-gray-200 bg-white"}`} data-testid="account-music">
                <Users size={28} className="mx-auto mb-2 text-purple-400" />
                <p className="font-anton text-4xl text-purple-400">{history?.unlocked_music ?? 0}</p>
                <p className={`text-xs mt-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>Music Unlocked</p>
              </div>
            </div>

            {/* Transaction History */}
            <h2 className="font-cinzel text-xl font-bold mb-4">Transaction History</h2>
            {!history ? (
              <div className="flex justify-center py-10">
                <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : history.transactions.length === 0 ? (
              <div className={`p-8 rounded-lg border text-center ${isDark ? "border-white/10 bg-white/[0.02]" : "border-gray-200 bg-gray-50"}`} data-testid="no-transactions">
                <Clock size={32} className="mx-auto mb-3 text-gray-500" />
                <p className={`text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>No transactions yet. Purchase credits or submit a Witness Stand case file to see activity here.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="transaction-list">
                {history.transactions.map((txn, i) => (
                  <div key={i} className={`p-4 rounded-lg border flex items-center justify-between ${isDark ? "border-white/10 bg-white/[0.02]" : "border-gray-200 bg-white"}`} data-testid={`txn-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        txn.type === "signup_bonus" ? "bg-green-500/20" :
                        txn.type === "credit_purchase" ? "bg-amber-500/20" :
                        txn.type === "witness_submission" ? "bg-blue-500/20" :
                        "bg-gray-500/20"
                      }`}>
                        {txn.type === "signup_bonus" ? <Gift size={18} className="text-green-400" /> :
                         txn.type === "credit_purchase" ? <Coins size={18} className="text-amber-400" /> :
                         <CreditCard size={18} className="text-gray-400" />}
                      </div>
                      <div>
                        <p className={`text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                          {txn.type === "signup_bonus" ? "Welcome Bonus" :
                           txn.type === "credit_purchase" ? `Credit Purchase` :
                           txn.description || txn.type}
                        </p>
                        <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                          {txn.description || (txn.type === "credit_purchase" ? `${txn.credits_total} credits` : "")}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {txn.amount > 0 && <p className="text-sm font-bold text-red-500">${txn.amount.toFixed(2)}</p>}
                      {txn.credits_total > 0 && <p className="text-xs text-amber-500">+{txn.credits_total} credits</p>}
                      <p className={`text-[10px] ${isDark ? "text-gray-600" : "text-gray-400"}`}>
                        {new Date(txn.created_at).toLocaleDateString()}
                      </p>
                      <p className={`text-[10px] ${
                        txn.payment_status === "paid" || txn.payment_status === "completed" ? "text-green-500" :
                        txn.payment_status === "pending" ? "text-amber-500" : "text-gray-500"
                      }`}>
                        {txn.payment_status}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </div>
  );
}
